import { Checkbox, Divider, Text, useBoolean, VStack } from '@chakra-ui/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useOutletContext } from 'react-router-dom';

import { AppError } from '../../../../framework/models/errors';
import { TypeUtils } from '../../../../framework/utils/UtilityTypes';
import { useCheckArea } from '../../../components/Check';
import DateMatter from '../../../components/DateMatter';
import FormFiles from '../../../components/form/FormFiles';
import { BackButton, SaveButton } from '../../../components/StyledButton';
import { useGlobalStateMachine } from '../../../GlobalStore';
import { useFormItems } from '../../../hooks/formItems';
import { useEngeneNavigate } from '../../../hooks/navigate';
import { usePostDailyMatterReport } from '../../../hooks/queries/dailyMatterReport';
import useToastHook from '../../../hooks/ToastHook';
import { EngeneFileObject } from '../../../models/EngeneFileObject';
import { DailyMatterReportSaveParam } from '../../../models/gen/dailyMatterReportSaveParam';
import { FileKind } from '../../../models/gen/fileKind';
import { FileUtils } from '../../../utils/FileUtils';
import { useSizeContext } from '../../SizeProvider';
import { WorkReportOutletContext } from './WorkReport';

/**
 *
 */
type ReportProps = {
  /**
   * 表示モード
   */
  mode: 'edit' | 'view';
};

export type ReportState = DailyMatterReportSaveParam & {
  /**
   * 作業報告書提出書
   */
  reports?: Array<EngeneFileObject>;
};

/**
 *
 * @returns
 */
const Report: React.FunctionComponent<ReportProps> = (props) => {
  const { actions, state } = useGlobalStateMachine();
  const navigate = useEngeneNavigate();
  const sizes = useSizeContext();
  const { toast } = useToastHook();

  const { CheckArea, checked, off: offCheck } = useCheckArea();
  const form = useFormContext<ReportState>();
  const { FormTextarea } = useFormItems(form);

  const [visibleCheckArea, { on, off }] = useBoolean(
    props.mode === 'edit' && (form.getValues('reports') || []).length > 0
  );

  const [checked1, { toggle: toggle1, off: off1 }] = useBoolean();
  const [checked2, { toggle: toggle2, off: off2 }] = useBoolean();
  const [checked3, { toggle: toggle3, off: off3 }] = useBoolean();

  React.useEffect(() => {
    if (visibleCheckArea) {
      off1();
      off2();
      off3();
      offCheck();
    }
  }, [visibleCheckArea]);

  const data = useOutletContext<WorkReportOutletContext>();
  const postDailyMatterReport = usePostDailyMatterReport();
  // const postAttachedFilePreview = usePostAttachedFilePreview();

  const onValid = (fieldValues: ReportState) => {
    const formData = new FormData();
    const saveFileNames: string[] = [];
    fieldValues.reports
      ?.filter((report) => !TypeUtils.isNilString(report.newFileName))
      ?.filter(
        (report) =>
          !data?.dailyMatterReport?.attachFiles
            ?.map((attachedFile) => attachedFile.fileName)
            .includes(report.newFileName)
      )
      .forEach((report) => saveFileNames.push(report.newFileName!));

    const metadata: DailyMatterReportSaveParam = {
      dailyMatterId: data?.dailyMatterReport?.dailyMatterId,
      dailyMatterVersion: data?.dailyMatterReport?.dailyMatterVersion,
      reportDocMemo: fieldValues.reportDocMemo,
      deleteFileNames: fieldValues.deleteFileNames || [],
      saveFileNames: saveFileNames,
    };
    formData.append(
      'metadata',
      new Blob([JSON.stringify(metadata)], { type: 'application/json' })
    );

    const validate = FileUtils.validate(
      fieldValues.reports,
      state.appConfig.facilitySettings,
      FileKind.ReportDoc
    );
    if (TypeUtils.isNonNilString(validate)) {
      toast(validate, 'warning');
      return;
    }

    fieldValues.reports
      ?.filter(
        (report) =>
          !data?.dailyMatterReport?.attachFiles
            ?.map((attachedFile) => attachedFile.fileName)
            .includes(report.newFileName || report.fileName) &&
          !TypeUtils.isNil(report.file)
      )
      .forEach((report) =>
        formData.append('files', report.file!, report.fileName)
      );

    actions.setGuard(true);
    postDailyMatterReport.mutate(formData, {
      onSuccess: () => {
        navigate();
        actions.setGuard(false);
      },
      onError: (error) => {
        toast(
          error instanceof AppError ? error : '作業報告書保存に失敗しました'
        );
        actions.setGuard(false);
      },
    });
  };

  const onDeleteFile = React.useCallback<Consumer<string>>(
    (filename) => {
      const deleteFileNames = form.getValues('deleteFileNames') || [];
      if (
        Array.isArray(deleteFileNames) &&
        !deleteFileNames.includes(filename)
      ) {
        deleteFileNames.push(filename);
      }
      form.setValue('deleteFileNames', deleteFileNames);
    },
    [form]
  );
  const onChangeFile = React.useCallback<Consumer<unknown[]>>(
    (files) => {
      if (props.mode === 'edit' && files.length > 0) on();
      else off();
    },
    [props.mode]
  );

  return (
    <VStack
      alignItems="start"
      as="form"
      fontSize={sizes.fontSize.rawMessage}
      onSubmit={form.handleSubmit(onValid)}
      width={sizes.width.styledMessageList}
    >
      <DateMatter
        customerName={data?.matterSummary?.customerName}
        dateString={data?.dailyMatterReport?.date}
        matterId={data?.matterSummary?.matterId}
        matterName={data?.matterSummary?.matterName}
      />
      <VStack
        backgroundColor="white"
        borderColor="gray.300"
        borderRadius="md"
        borderWidth={1}
        padding={2}
        width="100%"
      >
        <FormFiles
          form={form}
          mode={props.mode}
          name="reports"
          onDelete={onDeleteFile}
          onChange={onChangeFile}
          // onSelect={postAttachedFilePreview}
          prefix="作業報告書"
          startIndex={FileUtils.getNextIndex(
            data?.dailyMatterReport?.attachFiles?.map(
              (attachedFile) => attachedFile.fileName
            ) || []
          )}
          visibleFilename
        />
        <FormTextarea label="備考" mode={props.mode} name="reportDocMemo" />
      </VStack>
      <CheckArea
        additionalArea={
          <>
            <VStack fontSize={sizes.fontSize.listMessage}>
              <Text color="red" fontWeight="bold">
                !!! 注意 !!!
                一度確定すると原則訂正できません．報告書画像について、以下をチェックしてください
              </Text>
              <VStack
                alignItems="left"
                fontSize={sizes.fontSize.confirmMessage}
              >
                <Checkbox isChecked={checked1} onChange={toggle1}>
                  <Text fontSize={sizes.fontSize.confirmMessage}>
                    画像が鮮明であること
                  </Text>
                </Checkbox>
                <Checkbox isChecked={checked2} onChange={toggle2}>
                  <Text fontSize={sizes.fontSize.confirmMessage}>
                    報告書全体が写っていること
                  </Text>
                </Checkbox>
                <Checkbox isChecked={checked3} onChange={toggle3}>
                  <Text fontSize={sizes.fontSize.confirmMessage}>
                    手ブレしていないこと
                  </Text>
                </Checkbox>
              </VStack>
            </VStack>
            <Divider />
          </>
        }
        button={SaveButton}
        buttonProps={{
          isDisabled:
            TypeUtils.isNilNumber(data?.dailyMatterReport?.dailyMatterId) ||
            TypeUtils.isNilNumber(
              data?.dailyMatterReport?.dailyMatterVersion
            ) ||
            !checked ||
            !checked1 ||
            !checked2 ||
            !checked3,
        }}
        confirmMessage="上記の通りの作業内容であることを確認しました"
        hidden={
          !visibleCheckArea
          // props.mode === 'view' || form.getValues('reports')?.length === 0
          //  ||
          // (Array.isArray(data?.dailyMatterReport?.attachFiles) &&
          //   data!.dailyMatterReport!.attachFiles!.length > 0)
        }
      />
      <BackButton onClick={() => navigate(-1)} />
    </VStack>
  );
};

export default Report;
